import Decimal from "decimal.js";
import type {
  QuoteLine,
  QuoteDiscount,
  QuotePriceRounding,
  CalculateQuoteTotalsResult,
} from "../schemas/quotes";
import { convertAndRound, crossRate } from "../currency/conversion";
import { roundQuotePrice } from "../currency/rounding";
import {
  getMinorUnits,
  type CurrencyCode,
  type ExchangeRateSnapshot,
} from "../schemas/currency";

/**
 * An item line as totals see it: its amount is summed. The optional
 * `quantityBreaks` let the totals recompute the presentation-currency amount
 * from the effective unit price when a fully hydrated line is supplied (the
 * internal editor); public-quote lines omit it and fall back to rounding the
 * stored amount. `unitPriceOverride` is denominated in the quote's display
 * currency; `amount` and `unitPrice` stay in the base currency.
 */
export type QuoteTotalsItemLine = {
  type: "ITEM";
  amount: string | null;
  quantityBreaks?: ReadonlyArray<{
    quantity: string;
    unitPrice: string | null;
    unitPriceOverride: string | null;
  }>;
};

/** A discount line as totals see it: only its discount is applied. */
export type QuoteTotalsDiscountLine = {
  type: "DISCOUNT";
  discount: QuoteDiscount;
};

/**
 * The minimum a quote line must expose to be totalled.
 *
 * Deliberately narrower than `HydratedQuoteLine`: totals need a line's type and
 * its amount or discount, nothing else. Taking the minimum lets both the
 * internal quote and the customer-safe public quote — whose lines carry far
 * less — be totalled by the same function.
 */
export type QuoteTotalsLine =
  | QuoteTotalsItemLine
  | QuoteTotalsDiscountLine
  | { type: "SECTION" }
  | { type: "NOTE" };

/**
 * Currency context for converting a quote's line amounts into the quote
 * currency. Conversion uses the pinned exchange-rate `snapshot` so finalized
 * quotes reprice identically forever. When `quoteCurrency` is null or equal to
 * `baseCurrency`, or no snapshot is pinned, conversion is skipped and totals
 * stay in base currency.
 */
export interface QuoteCurrencyContext {
  quoteCurrency?: CurrencyCode | null;
  baseCurrency?: CurrencyCode | null;
  snapshot?: ExchangeRateSnapshot | null;
  quotePriceRounding?: QuotePriceRounding | null;
}

/**
 * Determines whether a quote should convert amounts into a non-base currency.
 * @param currency - The quote's currency context
 * @returns true when the quote currency differs from base and a snapshot exists
 */
export const isMultiCurrency = (currency?: QuoteCurrencyContext): boolean =>
  !!(
    currency?.quoteCurrency &&
    currency.baseCurrency &&
    currency.quoteCurrency !== currency.baseCurrency &&
    currency.snapshot
  );

/**
 * Converts a single line amount from the quote's base currency into the quote
 * currency using the pinned snapshot, rounded to the quote currency's ISO minor
 * unit (per-line rounding). Returns the amount unchanged (as a string) for
 * base-currency quotes.
 * @param amount - decimal string or number in the base currency
 * @param currency - the quote's currency context
 * @returns decimal string in the quote currency
 */
export const convertQuoteLineAmount = (
  amount: string | number,
  currency?: QuoteCurrencyContext,
): string => {
  const snapshot = currency?.snapshot;
  const from = currency?.baseCurrency;
  const to = currency?.quoteCurrency;
  if (!snapshot || !from || !to || from === to) {
    return String(amount);
  }
  const rate = crossRate(snapshot, from, to);
  return convertAndRound(String(amount), rate, to);
};

/**
 * Options controlling how a line's amounts are computed.
 */
export interface QuoteLineAmountOptions {
  /**
   * When true, the unit price is taken verbatim (never snapped to the rounding
   * increment) while the extended amount is still rounded to the display
   * currency's minor units. Used for manual unit-price overrides, which must
   * bypass only the coarse increment snap, not the currency-precision rounding.
   */
  skipUnitRounding?: boolean;
}

/**
 * Computes a line's presentation-currency unit price and extended amount, the
 * single source of truth for line amounts shared by backend hydration, backend
 * totals, and editor live math. When rounding is configured, only the unit is
 * snapped to the increment: the unit is converted (multi-currency) then rounded
 * once, and the extended (rounded-unit x quantity) is rounded to the display
 * currency's ISO minor units - never re-snapped to the increment. Passing
 * `skipUnitRounding` keeps the unit verbatim (for manual overrides) yet still
 * rounds the extended amount to the display currency's minor units. Without any
 * rounding config the extended is derived from the raw unit and converted per
 * line, while the unit converts at full cross-rate precision (never snapped to
 * minor units) since unit prices display at unit-price precision.
 * @param unitPrice - the effective unit price in base currency
 * @param quantity - the line quantity
 * @param currency - the quote's currency context (omit for base, no rounding)
 * @param options - optional amount-computation flags (e.g. skipUnitRounding)
 * @returns the presentation-currency unit price and extended amount
 */
export const calculateQuoteLineAmounts = (
  unitPrice: string,
  quantity: string,
  currency?: QuoteCurrencyContext,
  options?: QuoteLineAmountOptions,
): { unitPrice: string; amount: string } => {
  const rounding = currency?.quotePriceRounding ?? null;
  const multiCurrency = isMultiCurrency(currency);

  if (!rounding) {
    const amount = new Decimal(unitPrice).times(new Decimal(quantity)).toString();
    return {
      unitPrice: multiCurrency
        ? convertUnitPrice(unitPrice, currency)
        : unitPrice,
      amount: multiCurrency ? convertQuoteLineAmount(amount, currency) : amount,
    };
  }

  const presentationUnit = multiCurrency
    ? convertUnitPrice(unitPrice, currency)
    : unitPrice;
  const effectiveUnit = options?.skipUnitRounding
    ? presentationUnit
    : roundQuotePrice(presentationUnit, rounding);
  const amount = new Decimal(effectiveUnit)
    .times(new Decimal(quantity))
    .toFixed(quoteDisplayMinorUnits(currency), Decimal.ROUND_HALF_UP);
  return { unitPrice: effectiveUnit, amount };
};

/**
 * Resolves the ISO minor units of a quote's display currency: the quote
 * currency for multi-currency quotes, else the base currency, defaulting to 2
 * when the context carries no currency codes. Used to round extended amounts
 * and discounts when price rounding is configured.
 * @param currency - the quote's currency context
 * @returns the number of minor-unit decimal places
 */
export const quoteDisplayMinorUnits = (
  currency?: QuoteCurrencyContext,
): number => {
  if (isMultiCurrency(currency) && currency?.quoteCurrency) {
    return getMinorUnits(currency.quoteCurrency);
  }
  return currency?.baseCurrency ? getMinorUnits(currency.baseCurrency) : 2;
};

/**
 * Converts a base-currency unit price into the quote currency using the raw
 * cross-rate, keeping full precision (unlike convertQuoteLineAmount, which
 * rounds to the currency's minor unit). Rounding is then applied by the caller.
 * This is the single source of truth for the presentation-currency unit price
 * of a manual override, which is displayed at unit-price precision and must not
 * be snapped to minor units.
 * @param unitPrice - decimal string in the base currency
 * @param currency - the quote's currency context
 * @returns the converted unit price as a decimal string
 */
export const convertUnitPrice = (
  unitPrice: string,
  currency?: QuoteCurrencyContext,
): string => {
  const snapshot = currency?.snapshot;
  const from = currency?.baseCurrency;
  const to = currency?.quoteCurrency;
  if (!snapshot || !from || !to || from === to) {
    return unitPrice;
  }
  const rate = crossRate(snapshot, from, to);
  return new Decimal(unitPrice).times(new Decimal(rate)).toString();
};

/**
 * Converts a document-currency amount (a manual override or AMOUNT discount,
 * stored in the quote's display currency) into the base currency at full
 * precision. The snapshot's explicit quote-to-base pair wins when present -
 * rate tables are pairwise and can be asymmetric - otherwise the amount is
 * divided by the forward base-to-quote rate. Returns the amount unchanged when
 * the quote is not multi-currency.
 * @param amount - decimal string in the quote's display currency
 * @param currency - the quote's currency context
 * @returns the base-currency equivalent as a decimal string
 */
export const convertQuoteAmountToBase = (
  amount: string,
  currency?: QuoteCurrencyContext,
): string => {
  const parsedAmount = toDecimalOrZero(amount);
  const snapshot = currency?.snapshot;
  const base = currency?.baseCurrency;
  const quote = currency?.quoteCurrency;
  if (!snapshot || !base || !quote || base === quote) {
    return parsedAmount.toString();
  }
  const reverseRate = snapshot.rates[quote]?.[base];
  if (reverseRate !== undefined) {
    return parsedAmount.times(new Decimal(reverseRate)).toString();
  }
  return parsedAmount
    .dividedBy(new Decimal(crossRate(snapshot, base, quote)))
    .toString();
};

/**
 * Parses a decimal string, treating blank or non-numeric input as zero. Editor
 * cells commit blank strings when a value is cleared, so a defensive parse keeps
 * the totals from throwing while a user blanks or re-enters an amount.
 * @param amount - the decimal string to parse
 * @returns the parsed Decimal, or zero when the input is blank or non-numeric
 */
const toDecimalOrZero = (amount: string): Decimal => {
  try {
    return new Decimal(amount);
  } catch {
    return new Decimal(0);
  }
};

/**
 * The display-currency extended amount of a manual unit-price override:
 * override x qty rounded to the display currency's ISO minor units. The
 * override is stored in the display currency, so no FX conversion applies.
 * @param unitPriceOverride - the manual override in the display currency
 * @param quantity - the line quantity
 * @param currency - the quote's currency context
 * @returns the extended amount as a decimal string at minor units
 */
export const overrideDisplayAmount = (
  unitPriceOverride: string,
  quantity: string,
  currency?: QuoteCurrencyContext,
): string =>
  new Decimal(unitPriceOverride)
    .times(new Decimal(quantity))
    .toFixed(quoteDisplayMinorUnits(currency), Decimal.ROUND_HALF_UP);

/**
 * Computes quote totals from already-hydrated lines, converting each line into
 * the quote currency and rounding per line so the displayed lines always sum
 * exactly to the displayed total. Manual unit-price overrides and AMOUNT
 * discounts are denominated in the display currency and enter the totals
 * verbatim (no FX conversion, no increment snap). When rounding is configured,
 * non-overridden item lines derive their presentation amount from the rounded
 * unit price, the subtotal is the plain sum of those line totals, and both
 * discount types round to the display currency's minor units - the increment
 * never applies past the unit price. Returns nulls when any item line lacks an
 * amount.
 * @param hydratedLines - the quote's hydrated lines
 * @param currency - the quote's currency context (omit for base-currency totals)
 * @returns the subtotal, subtotal-with-discounts, and total (or nulls)
 */
export const calculateHydratedQuoteTotals = (
  hydratedLines: readonly QuoteTotalsLine[],
  currency?: QuoteCurrencyContext,
): CalculateQuoteTotalsResult => {
  const itemLines = hydratedLines.filter((l) => l.type === "ITEM");
  const discountLines = hydratedLines.filter((l) => l.type === "DISCOUNT");

  if (!itemLines.every((l) => l.amount)) {
    return { subtotal: null, subtotalWithDiscounts: null, total: null };
  }

  const multiCurrency = isMultiCurrency(currency);
  const rounding = currency?.quotePriceRounding ?? null;

  const subtotal = itemLines.reduce(
    (acc, line) => acc.plus(new Decimal(lineDisplayAmount(line, currency))),
    new Decimal(0),
  );

  const subtotalWithDiscounts = discountLines.reduce((acc, line) => {
    if (line.discount.type === "PERCENTAGE") {
      const raw = subtotal
        .times(new Decimal(line.discount.percentage || "0"))
        .dividedBy(100);
      return acc.minus(
        rounding
          ? new Decimal(
              raw.toFixed(quoteDisplayMinorUnits(currency), Decimal.ROUND_HALF_UP),
            )
          : raw,
      );
    }
    return acc.minus(
      new Decimal(discountDisplayAmount(line.discount, currency)),
    );
  }, subtotal);

  const minorUnits =
    multiCurrency && currency?.quoteCurrency
      ? getMinorUnits(currency.quoteCurrency)
      : 2;

  return {
    subtotal: subtotal.toFixed(minorUnits),
    subtotalWithDiscounts: subtotalWithDiscounts.toFixed(minorUnits),
    total: subtotalWithDiscounts.toFixed(minorUnits),
  };
};

/**
 * Resolves an item line's presentation-currency extended amount. A manual
 * unit-price override is denominated in the display currency, so its amount is
 * override x qty at the display currency's minor units - never converted from
 * base (rate tables can be asymmetric, so a base round-trip would corrupt the
 * customer-facing figure) and never snapped to the rounding increment. With
 * rounding on, non-overridden lines recompute from the (first) quantity
 * break's derived unit price so the amount equals rounded-unit x qty at the
 * display currency's minor units. With rounding off (and no override on a
 * multi-currency quote) the line's stored amount converts per line as before.
 * @param line - the hydrated item line
 * @param currency - the quote's currency context
 * @returns the line's presentation-currency extended amount as a string
 */
const lineDisplayAmount = (
  line: QuoteTotalsItemLine,
  currency?: QuoteCurrencyContext,
): string => {
  const brk = line.quantityBreaks?.[0];
  if (
    brk?.unitPriceOverride &&
    (isMultiCurrency(currency) || currency?.quotePriceRounding)
  ) {
    return overrideDisplayAmount(brk.unitPriceOverride, brk.quantity, currency);
  }
  const base = line.amount ?? "0";
  const legacyAmount = isMultiCurrency(currency)
    ? convertQuoteLineAmount(base, currency)
    : base;
  if (!currency?.quotePriceRounding) {
    return legacyAmount;
  }
  if (!brk || brk.unitPrice === null) {
    return new Decimal(legacyAmount).toFixed(
      quoteDisplayMinorUnits(currency),
      Decimal.ROUND_HALF_UP,
    );
  }
  return calculateQuoteLineAmounts(brk.unitPrice, brk.quantity, currency)
    .amount;
};

/**
 * Resolves an AMOUNT discount's presentation-currency value. The stored amount
 * is denominated in the display currency, so it applies verbatim - no FX
 * conversion - rounded to the display currency's minor units on multi-currency
 * quotes and when rounding is on so discount rows tie to the totals they are
 * subtracted from. The rounding increment never applies to discounts.
 * @param discount - the discount (only AMOUNT is resolved)
 * @param currency - the quote's currency context
 * @returns the presentation-currency discount amount as a string
 */
const discountDisplayAmount = (
  discount: QuoteDiscount,
  currency?: QuoteCurrencyContext,
): string => {
  if (discount.type !== "AMOUNT") {
    return "0";
  }
  const amount = discount.amount || "0";
  return isMultiCurrency(currency) || currency?.quotePriceRounding
    ? new Decimal(amount).toFixed(
        quoteDisplayMinorUnits(currency),
        Decimal.ROUND_HALF_UP,
      )
    : amount;
};

/**
 * Computes sequential line numbers for quote lines.
 * Only ITEM lines receive incrementing numbers (1, 2, 3, ...);
 * SECTION, NOTE, and DISCOUNT lines receive null since they are markup, not billable items.
 * @param lines - The ordered quote lines (must have a `type` field)
 * @returns Array of line numbers (or null) parallel to the input array
 */
export const computeLineNumbers = (
  lines: ReadonlyArray<Pick<QuoteLine, "type">>,
): (number | null)[] => {
  let count = 0;
  return lines.map((line) => {
    if (line.type === "ITEM") {
      count += 1;
      return count;
    }
    return null;
  });
};
